import { useCallback, useState } from "react";
import { computeAccrualLineAmount } from "@common/helpers/accrual-line-item-amount.helper";
import { createAccrualImporterRow } from "./createAccrualImporterRow";
import { createAccrualImporterRowId } from "./createAccrualImporterRowId";
import { AccrualImporterRow } from "./types";

function createEmptyRow(): AccrualImporterRow {
    return createAccrualImporterRow({
        _id: createAccrualImporterRowId(),
    });
}

export function isFirstRowInGroup(
    rows: AccrualImporterRow[],
    rowIndex: number,
): boolean {
    const extractionId = rows[rowIndex]?.extractionId;
    for (let i = rowIndex - 1; i >= 0; i--) {
        if (rows[i].extractionId === extractionId) {
            return false;
        }
    }
    return true;
}

export interface AccrualImporterLineItem {
    lineId?: string;
    productOrService: string | null;
    catalogItemId: string | null;
    catalogItemPriceId: string | null;
    description: string;
    amount: number;
    quantityDecimal: number | null;
    unitAmount: number | null;
    classIds: string[] | null;
    accountId: string | null;
    accrualAccountId: string | null;
}

export interface AccrualImporterGroup {
    /**
     * The persisted bill / invoice id this group corresponds to. Set when
     * every row in the group shares the same `accrualId` (i.e. the user did
     * not split or merge groups). Drives patch vs create routing in submit.
     */
    accrualId?: string;
    invoiceNumber: string;
    createdDate: string;
    counterpartyId: string | null;
    dueDate: string;
    entityId: number | null;
    financialAccountId: number | null;
    status?: string;
    tax?: number;
    lines: AccrualImporterLineItem[];
}

function buildLineItem(
    row: AccrualImporterRow,
): AccrualImporterLineItem | null {
    const amount = computeAccrualLineAmount({
        quantity: row.quantity,
        unitPrice: row.unitPrice,
        amount: row.amount,
    });
    if (amount === null) {
        return null;
    }

    return {
        lineId: row.lineId,
        productOrService: row.productOrService || null,
        catalogItemId: row.catalogItemId,
        catalogItemPriceId: row.catalogItemPriceId,
        description: row.description,
        amount,
        quantityDecimal: row.quantity,
        unitAmount: row.unitPrice,
        classIds: row.classIds,
        accountId: row.accrualLedger || null,
        accrualAccountId: row.accrualAccountId || null,
    };
}

export function groupRowsByInvoice(
    rows: AccrualImporterRow[],
): AccrualImporterGroup[] {
    const groupMap = new Map<string, AccrualImporterGroup>();

    for (const row of rows) {
        const key = row.invoiceNumber || row._id;
        const existing = groupMap.get(key);
        const lineItem = buildLineItem(row);
        if (existing) {
            if (lineItem) {
                existing.lines.push(lineItem);
            }
            if (
                existing.accrualId !== undefined &&
                row.accrualId !== existing.accrualId
            ) {
                existing.accrualId = undefined;
            }
        } else {
            groupMap.set(key, {
                accrualId: row.accrualId,
                invoiceNumber: row.invoiceNumber,
                createdDate: row.createdDate,
                counterpartyId: row.counterpartyId,
                dueDate: row.dueDate,
                entityId: row.entityId,
                financialAccountId: row.financialAccountId,
                status: row.status,
                tax: row.tax,
                lines: lineItem ? [lineItem] : [],
            });
        }
    }

    return Array.from(groupMap.values());
}

export const SHARED_FIELDS: ReadonlyArray<keyof AccrualImporterRow> = [
    "createdDate",
    "counterpartyId",
    "counterpartyName",
    "invoiceNumber",
    "memo",
    "totalAmount",
    "dueDate",
    "entityId",
    "financialAccountId",
    "tax",
];

export function propagateSharedField({
    rows,
    prevRows,
    rowIndex,
    field,
}: {
    rows: AccrualImporterRow[];
    prevRows: AccrualImporterRow[];
    rowIndex: number;
    field: keyof AccrualImporterRow;
}): void {
    if (!SHARED_FIELDS.includes(field)) {
        return;
    }

    const oldRow = prevRows[rowIndex];
    if (!oldRow) {
        return;
    }

    if (!isFirstRowInGroup(prevRows, rowIndex)) {
        return;
    }

    const extractionId = oldRow.extractionId;
    const newValue = rows[rowIndex][field];
    for (const [i, row] of rows.entries()) {
        if (i !== rowIndex && row.extractionId === extractionId) {
            rows[i] = { ...row, [field]: newValue };
        }
    }

    if (field === "entityId" && oldRow.entityId !== newValue) {
        for (const [i, row] of rows.entries()) {
            if (row.extractionId === extractionId) {
                rows[i] = {
                    ...rows[i],
                    productOrService: "",
                    catalogItemId: null,
                    catalogItemPriceId: null,
                    accrualLedger: "",
                    accrualAccountId: "",
                };
            }
        }
    }
}

export function useAccrualImporterState(initialRows?: AccrualImporterRow[]) {
    const [rows, setRows] = useState<AccrualImporterRow[]>(
        () => initialRows ?? [createEmptyRow()],
    );

    const addLineItemToGroup = useCallback((rowIndex: number) => {
        setRows((prev) => {
            const sourceRow = prev[rowIndex];
            const newRow = createEmptyRow();
            newRow.accrualId = sourceRow.accrualId;
            newRow.extractionId = sourceRow.extractionId;
            newRow.createdDate = sourceRow.createdDate;
            newRow.counterpartyId = sourceRow.counterpartyId;
            newRow.counterpartyName = sourceRow.counterpartyName;
            newRow.invoiceNumber = sourceRow.invoiceNumber;
            newRow.memo = sourceRow.memo;
            newRow.dueDate = sourceRow.dueDate;
            newRow.entityId = sourceRow.entityId;
            newRow.financialAccountId = sourceRow.financialAccountId;
            newRow.totalAmount = sourceRow.totalAmount;
            newRow.tax = sourceRow.tax;
            newRow.extractionWarnings = sourceRow.extractionWarnings;

            let insertAt = rowIndex + 1;
            while (
                insertAt < prev.length &&
                prev[insertAt].extractionId === sourceRow.extractionId
            ) {
                insertAt++;
            }

            const next = [...prev];
            next.splice(insertAt, 0, newRow);
            return next;
        });
    }, []);

    const removeRow = useCallback((rowIndex: number) => {
        setRows((prev) => {
            if (prev.length <= 1) {
                return prev;
            }
            const next = [...prev];
            next.splice(rowIndex, 1);
            return next;
        });
    }, []);

    const updateRow = useCallback(
        (
            rowIndex: number,
            field: keyof AccrualImporterRow,
            value: AccrualImporterRow[keyof AccrualImporterRow],
        ) => {
            setRows((prev) => {
                const next = [...prev];
                next[rowIndex] = { ...next[rowIndex], [field]: value };
                propagateSharedField({
                    rows: next,
                    prevRows: prev,
                    rowIndex,
                    field,
                });
                return next;
            });
        },
        [],
    );

    const addNewInvoice = useCallback(() => {
        setRows((prev) => [...prev, createEmptyRow()]);
    }, []);

    const createRow = useCallback(
        (): AccrualImporterRow => createEmptyRow(),
        [],
    );

    return {
        rows,
        setRows,
        addLineItemToGroup,
        removeRow,
        updateRow,
        addNewInvoice,
        createRow,
    };
}
